import styled, { css } from 'styled-components';
import { exFont } from '../../shared/typography';

// Questions step (tplA 504–583) and the form primitives the Marking step
// shares. Colours come only from ExamsScope's tokens.

export const StepPage = styled.div`
  display: flex;
  justify-content: center;
  padding: 28px 28px 40px;
  &[data-testid='questions-step'],
  &[data-layout='questions'] {
    padding: 28px 24px 44px;
  }
  &[data-testid='marking-step'],
  &[data-layout='marking'] {
    padding: 24px 24px 36px;
  }
`;

export const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 860px;
`;

export const QuestionsColumn = styled(StepColumn)`
  max-width: 832px;
  gap: 16px;
  & > :first-child {
    gap: 4px;
    h2,
    p {
      line-height: normal;
    }
  }
`;

export const StepHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const H2 = styled.h2`
  ${exFont(700, 'xxl')}
  margin: 0;
  color: var(--ex-ink);
`;

export const Sub = styled.p`
  ${exFont(400, 'm')}
  margin: 0;
  color: var(--ex-ink-strong);
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 22px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: 14px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: ${({ $grow }) => ($grow ? '1 1 0' : '0 0 auto')};
`;

// Validation lines supply the gaps between sections on this step.
export const QuestionsCard = styled(Card)`
  gap: 0;
  /* The path question's 16px validation line + 4px = the design's 20px. */
  padding-bottom: 4px;
`;

export const SubjectField = styled(Field)`
  flex: 0 1 280px;
  max-width: 100%;
`;

export const Row = styled.div`
  display: flex;
  align-items: ${({ $align }) => $align || 'flex-end'};
  gap: 12px;
  flex-wrap: wrap;
`;

// Title (tplA 510): label 600 14px, 7px above a 44px input. Its validation
// line takes the first 16px of the 30px gap above Year/Subject.
export const DetailsTitle = styled(Field)`
  gap: 0;
  margin: 16px 0 14px;
  & > label {
    margin-bottom: 7px;
  }
  & > input {
    height: 44px;
    padding: 0 14px;
    border-radius: 10px;
    font-size: 15px;
  }
`;

// Year · Subject · State (tplA 519–525): 160px · up to 280px · auto, 12px
// apart. Labels 500 13px; selects 44px, radius 10, 15px text, a 16px chevron.
// Each validation line fills the 16px gap above "Questions".
export const DetailsRow = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 280px) auto;
  column-gap: 12px;
  align-items: start;
  & > div {
    gap: 0;
  }
  & > div > label {
    ${exFont(500, 's')}
    line-height: normal;
    margin-bottom: 6px;
    color: var(--ex-ink-strong);
  }
  select {
    width: 100%;
    min-width: 0;
    height: 44px;
    padding: 0 42px 0 12px;
    border-radius: 10px;
    font-size: 15px;
    background-color: var(--ex-surface);
    color: var(--ex-ink);
  }
  &&& select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236c6875' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-position: right 16px center;
  }
  select[data-empty='true'] {
    color: var(--ex-ink-muted);
  }
  select:disabled {
    background-color: var(--ex-surface);
    opacity: 0.55;
    cursor: not-allowed;
  }
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// State (tplA 522–524): "NSW Update" on a dashed underline, centred on the
// selects' row; Update swaps in a 110px select.
export const StateCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  margin-top: 23px;
  & > select {
    width: 110px;
  }
`;

export const StateValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding-bottom: 2px;
  border-bottom: 1.5px dashed var(--ex-ink);
  & > span {
    ${exFont(600, 14)}
    color: var(--ex-ink);
  }
  & > button {
    ${exFont(600, 13.5)}
  }
  & > button:hover:not(:disabled) {
    text-decoration: none;
  }
`;

export const Label = styled.label`
  ${exFont(600, 14)}
  line-height: normal;
  color: var(--ex-ink);
`;

export const Required = styled.span`
  color: var(--ex-bad);
`;

export const Hint = styled.p`
  ${exFont(400, 12.5)}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink-2);
`;

const control = css`
  ${exFont(400, 'm')}
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface);
  color: var(--ex-ink);
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
  &:disabled {
    background-color: var(--ex-surface-2);
    color: var(--ex-ink-muted);
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  ${control}
  width: 100%;
`;

export const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${control}
  min-width: 160px;
`;

export const TextArea = styled.textarea`
  ${control}
  width: 100%;
  min-height: 96px;
  resize: vertical;
`;

export const NumberInput = styled.input`
  ${control}
  width: 72px;
  text-align: center;
`;

export const LinkButton = styled.button`
  ${exFont(600, 's')}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--ex-radius-item);
  background: transparent;
  color: var(--ex-ink-muted);
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--ex-surface-2);
    color: var(--ex-ink);
  }
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: not-allowed;
  }
`;

export const Chip = styled.span`
  ${exFont(500, 'xs')}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  padding: 4px 10px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-purple-100);
  color: var(--ex-purple);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChipOpen = styled.button`
  ${exFont(500, 'xs')}
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export const ChipThumb = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const ChipRemove = styled.button`
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
`;

export const Note = styled.div`
  ${exFont(400, 's')}
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--ex-radius-item);
  ${({ $tone }) =>
    $tone === 'warn'
      ? css`
          background: var(--ex-warn-bg);
          color: var(--ex-warn-ink);
        `
      : $tone === 'ok'
      ? css`
          background: var(--ex-ok-bg);
          color: var(--ex-ok-ink);
        `
      : $tone === 'bad'
      ? css`
          background: var(--ex-bad-bg);
          color: var(--ex-bad-ink);
        `
      : css`
          background: var(--ex-purple-100);
          color: var(--ex-ink);
        `}
`;

export const NoteText = styled.p`
  margin: 0;
  flex: 1;
`;

// -------------------------------------------------------------- paper banner
// Question paper banner (tplA 507–509): idle drop zone, busy card, done strip.
export const PaperDrop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border: 1.5px dashed
    ${({ $over }) => ($over ? 'var(--ex-purple)' : '#c9bff0')};
  border-radius: 12px;
  background: ${({ $over }) => ($over ? 'var(--ex-purple-100)' : '#fbfafd')};
  transition: background 0.15s, border-color 0.15s;
  & > span:last-of-type {
    ${exFont(400, 12)}
    color: var(--ex-ink-2);
    white-space: nowrap;
  }
`;

export const OptionalTag = styled.span`
  ${exFont(700, 11)}
  line-height: normal;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 9px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 999px;
  background: var(--ex-surface);
  color: #3d3847;
`;

export const BannerText = styled.p`
  ${exFont(400, 14)}
  line-height: 1.45;
  margin: 0;
  flex: 1;
  min-width: 220px;
  color: #3d3847;
  & > button {
    font-size: inherit;
    line-height: inherit;
    vertical-align: baseline;
  }
  & > button:hover:not(:disabled) {
    text-decoration: none;
  }
`;

export const PaperBusy = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1.5px solid #c9bff0;
  border-radius: 14px;
  background: var(--ex-surface);
  & > p {
    ${exFont(600, 14.5)}
    margin: 0;
    color: var(--ex-ink);
  }
`;

export const PaperSpinner = styled.span`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 3px solid #e4d3fb;
  border-top-color: var(--ex-purple);
  animation: ex-paper-spin 0.9s linear infinite;
  @keyframes ex-paper-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const PaperDone = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-radius: 14px;
  background: ${({ $tone }) =>
    $tone === 'warn' ? '#fdf7dc' : $tone === 'bad' ? '#fde2e2' : '#e3f5ec'};
  & + &,
  ${PaperDrop} + &,
  & + ${PaperDrop} {
    margin-top: 8px;
  }
  & > svg {
    flex: 0 0 auto;
  }
  & > p {
    ${exFont(500, 14)}
    margin: 0;
    flex: 1;
    min-width: 220px;
    color: ${({ $tone }) =>
      $tone === 'warn' ? '#5c4a00' : $tone === 'bad' ? '#8f1d16' : '#1f6440'};
  }
  & > p b {
    font-weight: 700;
  }
  & > button {
    ${exFont(600, 13.5)}
    padding: 0;
    border: none;
    background: none;
    color: #1f6440;
    text-decoration: underline;
    cursor: pointer;
  }
  & > button:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const Muted = styled.span`
  ${exFont(400, 'xs')}
  color: var(--ex-ink-muted);
`;

export const SmallSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid var(--ex-purple-200);
  border-top-color: var(--ex-purple);
  border-radius: 50%;
  animation: ex-spin 0.9s linear infinite;
  @keyframes ex-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ------------------------------------------------------------ question cards
export const QuestionsHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

export const TotalText = styled.span`
  ${exFont(600, 14)}
  line-height: normal;
  margin-left: auto;
  color: var(--ex-ink);
`;

// The dashed well that holds the cards and the add buttons (tplA 533).
export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px dashed #dccdf5;
  border-radius: 14px;
  background: #fbf8fe;
  &:empty {
    display: none;
  }
`;

// A question card (tplA 536): 3px top border that turns purple on a drop.
export const QCard = styled.div`
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line)')};
  border-top: 3px solid
    ${({ $invalid, $over }) =>
      $over
        ? 'var(--ex-purple)'
        : $invalid
        ? 'var(--ex-bad)'
        : 'var(--ex-line)'};
  border-radius: 12px;
  overflow: hidden;
  background: var(--ex-surface);
  ${({ $dragging }) =>
    $dragging &&
    css`
      box-shadow: var(--ex-shadow-2);
      opacity: 0.9;
    `}
`;

export const QHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid
    ${({ $open }) => ($open ? 'var(--ex-line)' : 'transparent')};
`;

// Duplicate / Delete (15px glyphs in a 4px hit pad) and the 26px collapse disc.
export const HeadIcon = styled.button`
  display: flex;
  flex: 0 0 auto;
  padding: 4px;
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 1px;
  }
`;

export const CollapseDisc = styled.button`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: var(--ex-line);
  cursor: pointer;
  & > svg {
    transition: transform 0.15s;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const DragHandle = styled.span`
  display: flex;
  flex: 0 0 auto;
  color: var(--ex-ink-faint);
  cursor: grab;
  touch-action: none;
`;

export const QTitle = styled.span`
  ${exFont(700, 14)}
  line-height: normal;
  color: var(--ex-ink);
  white-space: nowrap;
`;

// The multi-part glyph "{ ⁝ }" (tplA 537, 567).
export const MultiTag = styled.span`
  ${exFont(700, 14)}
  line-height: normal;
  letter-spacing: -1px;
  color: var(--ex-purple);
`;

export const Preview = styled.span`
  ${exFont(400, 13.5)}
  line-height: normal;
  flex: 1;
  min-width: 0;
  color: var(--ex-ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Spacer = styled.span`
  flex: 1;
`;

export const MarksTag = styled.span`
  ${exFont(600, 12.5)}
  line-height: normal;
  color: var(--ex-ink-2);
  white-space: nowrap;
`;

export const QBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $part }) => ($part ? '14px' : '16px')};
  padding: ${({ $part }) => ($part ? '14px 16px' : '16px')};
`;

export const FieldHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FieldTitle = styled.p`
  ${exFont(600, 's')}
  [data-testid='questions-step'] & {
    font-size: 14px;
    line-height: normal;
    color: var(--ex-ink);
  }
  margin: 0;
  color: var(--ex-ink-strong);
`;

export const EditorFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// The editor's footer row inside the same box (tplA 542): stimulus chips,
// "+ Stimulus", then Marks on the right.
export const QuestionEditorFooter = styled(EditorFooter)`
  padding: 10px 12px;
  border: 1px solid var(--ex-line-strong);
  border-top: 1px solid var(--ex-line);
  border-radius: 0 0 10px 10px;
  background: var(--ex-surface);
  & > div {
    gap: 8px;
  }
  & > div > button:last-child {
    ${exFont(600, 13.5)}
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    border: 1.5px solid var(--ex-purple);
    border-radius: 9px;
    background: var(--ex-surface);
  }
  & > div > button:last-child:hover:not(:disabled) {
    text-decoration: none;
  }
  & label {
    ${exFont(600, 14)}
    line-height: normal;
    color: var(--ex-ink);
  }
  & input[inputmode='numeric'] {
    ${exFont(700, 15)}
    width: 56px;
    height: 38px;
    padding: 0;
    border-radius: 9px;
  }
`;

// The design's editor (tplA 540–541): one box, radius 10, a 34px toolbar of
// 16px glyphs 18px apart, and a 70px writing area in 14.5px text.
const questionEditor = css`
  .question-quill-shell {
    border-color: var(--ex-line-strong);
    border-radius: 10px 10px 0 0;
  }
  .question-quill-shell:focus-within {
    border-color: var(--ex-line-strong);
  }
  .question-quill-shell .ql-toolbar.ql-snow {
    min-height: 34px !important;
    padding: 8px !important;
    gap: 18px;
    border-bottom-color: var(--ex-line) !important;
  }
  .question-quill-shell .ql-toolbar.ql-snow .ql-formats {
    gap: 18px !important;
  }
  .question-quill-shell .ql-toolbar.ql-snow button {
    width: 16px !important;
    height: 18px !important;
    color: #3d3847;
  }
  .question-quill-shell .ql-toolbar.ql-snow button svg {
    width: 16px;
    height: 16px;
  }
  .question-quill-shell .ql-toolbar.ql-snow .ql-stroke {
    stroke: #3d3847;
  }
  .question-quill-shell .ql-toolbar.ql-snow .ql-fill {
    fill: #3d3847;
  }
  .question-quill-shell .ql-container.ql-snow {
    font-size: 14.5px;
  }
  .question-quill-shell .ql-container .ql-editor {
    min-height: 70px;
    padding: 10px 12px !important;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--ex-ink);
  }
  .question-quill-shell .ql-container .ql-editor.ql-blank::before {
    left: 12px !important;
  }
`;

// Sub-questions well inside a multi-part card (tplA 548–562).
export const PartsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #f3f0f8;
  [data-testid='questions-step'] & > p:first-child {
    ${exFont(600, 14)}
    line-height: normal;
    color: var(--ex-purple);
  }
  button[data-part-add] {
    ${exFont(600, 14)}
    height: 42px;
    padding: 0 18px;
    border-radius: 10px;
    box-shadow: none;
  }
`;

export const AddRow = styled.div`
  padding: 6px 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  > button {
    ${exFont(600, 14)}
    height: 42px;
    padding: 0 22px;
    border-radius: 999px;
    background: var(--ex-surface);
    border: 1.5px solid var(--ex-purple);
    color: var(--ex-purple);
  }
`;

export const Or = styled.span`
  ${exFont(500, 's')}
  color: var(--ex-ink-2);
`;

// --------------------------------------------------------------- path cards
export const PathOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

export const PathCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  text-align: left;
  border: 1.5px solid
    ${({ $active, $invalid }) =>
      $active
        ? 'var(--ex-purple)'
        : $invalid
        ? 'var(--ex-bad)'
        : 'var(--ex-line)'};
  border-radius: 12px;
  background: ${({ $active }) =>
    $active ? 'var(--ex-selected-tint)' : 'var(--ex-surface)'};
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const Radio = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-ink-faint)')};
  box-shadow: ${({ $active }) =>
    $active ? 'inset 0 0 0 3px var(--ex-surface)' : 'none'};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
`;

export const PathTitle = styled.p`
  ${exFont(600, 15)}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink);
`;

export const PathNote = styled.p`
  ${exFont(400, 13.5)}
  line-height: 1.45;
  margin: 3px 0 0;
  color: #4a4458;
`;

// ------------------------------------------------------------------ toasts
export const UndoToastRoot = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--ex-ink);
`;

// ------------------------------------------------------------ stimulus form
// A stimulus chip in the editor footer (tplA 542): kind tag · name · ✕.
export const StimChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 5px 8px 5px 6px;
  border: 1px solid var(--ex-line);
  border-radius: 9px;
  background: #fbfafd;
  & > button:first-of-type {
    ${exFont(500, 13)}
    min-width: 0;
    padding: 0;
    border: none;
    background: none;
    color: var(--ex-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
  & > button:first-of-type:hover {
    text-decoration: underline;
  }
`;

export const StimKind = styled.span`
  ${exFont(700, 10.5)}
  line-height: normal;
  flex: 0 0 auto;
  padding: 2px 7px;
  border-radius: 5px;
  background: var(--ex-purple-100);
  color: var(--ex-purple);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const StimRemove = styled.button`
  display: flex;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

export const StimulusPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-tint);
`;

export const HiddenInput = styled.input`
  display: none;
`;

// Align question labels beside their controls, as in the exam design.
export const QuestionField = styled(Field)`
  display: grid;
  grid-template-columns: minmax(140px, 220px) minmax(0, 1fr);
  column-gap: 16px;
  row-gap: 0;
  & > :first-child {
    grid-column: 1;
    grid-row: 1 / span 4;
  }
  & > :not(:first-child) {
    grid-column: 2;
  }
  ${questionEditor}
`;

// A sub-question puts its label above the editor (tplA 554).
export const PartQuestionField = styled(Field)`
  gap: 0;
  & > :first-child {
    margin-bottom: 8px;
  }
  ${questionEditor}
`;

// Task type (tplA 539): label + hint beside a 44px select in 500 14.5px.
export const TaskRow = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 220px) minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  && > select {
    ${exFont(500, 14.5)}
    width: 100%;
    height: 44px;
    padding: 0 36px 0 14px;
    border-radius: 10px;
    background-position: right 14px center;
  }
`;
